import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Table, Button, Modal, Tabs, Tag, Spin, Input, Drawer, Tooltip as AntTooltip, Checkbox, InputNumber, Badge } from 'antd'
import {
  EnvironmentOutlined, TeamOutlined, BookOutlined, SearchOutlined, RiseOutlined,
  CheckCircleOutlined, CloseCircleOutlined, FileSearchOutlined, BankOutlined,
  GlobalOutlined, BarChartOutlined, SolutionOutlined, FilterOutlined,
} from '@ant-design/icons'
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps'
import indiaStatesRaw from '../indiaStates.json'
import adminRequest from 'utils/adminRequest'
import {
  ComposedChart, BarChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import styled from 'styled-components'

const { TabPane } = Tabs

const toArr = v => Array.isArray(v) ? v : (v != null && v !== '' ? [v] : [])

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
]

const ASSESSED_OPTIONS = [
  { value: 'yes', label: 'Assessed' },
  { value: 'no',  label: 'Not Assessed' },
]

const PROFILE_READY_OPTIONS = [
  { value: 'yes', label: 'Has Profile Ready Students' },
  { value: 'no',  label: 'No Profile Ready Students' },
]

const FILTER_CATS = [
  { key: 'state',       label: 'State' },
  { key: 'city',        label: 'City' },
  { key: 'dept',        label: 'Department' },
  { key: 'degree',      label: 'Degree' },
  { key: 'gender',      label: 'Gender' },
  { key: 'minMarks',    label: 'Min Marks %' },
  { key: 'maxMarks',    label: 'Max Marks %' },
  { key: 'maxArrears',  label: 'Max Arrears' },
  { key: 'minStudents', label: 'Min Students' },
  { key: 'assessed',    label: 'Assessment Status' },
  { key: 'profileReady', label: 'Profile Ready' },
]

const EMPTY_PENDING = {
  state: [], city: [], dept: [], degree: [], gender: [],
  minMarks: undefined, maxMarks: undefined,
  maxArrears: undefined, minStudents: undefined,
  assessed: [], profileReady: [],
}

const STATE_ALIASES = {
  'orissa': 'odisha', 'uttaranchal': 'uttarakhand',
  'pondicherry': 'puducherry', 'nct of delhi': 'delhi',
  'jammu and kashmir': 'jammu & kashmir',
  'dadra and nagar haveli': 'dadra & nagar haveli',
}

const TOP10_COLORS       = ['#3249D7','#0891b2','#7c3aed','#059669','#d97706','#e11d48','#0d9488','#ea580c','#1d4ed8','#15803d']
const CITY_BUBBLE_COLORS = ['#e11d48','#7c3aed','#d97706','#059669','#0891b2','#3249D7','#db2777','#0d9488','#ea580c','#15803d']

const CITY_COORDS = {
  'chennai':           [80.2707, 13.0827],
  'mumbai':            [72.8777, 19.0760],
  'delhi':             [77.1025, 28.7041],
  'new delhi':         [77.2090, 28.6139],
  'bangalore':         [77.5946, 12.9716],
  'bengaluru':         [77.5946, 12.9716],
  'hyderabad':         [78.4867, 17.3850],
  'coimbatore':        [76.9558, 11.0168],
  'madurai':           [78.1198,  9.9252],
  'trichy':            [78.7047, 10.7905],
  'tiruchirappalli':   [78.7047, 10.7905],
  'salem':             [78.1460, 11.6643],
  'erode':             [77.7172, 11.3410],
  'tirunelveli':       [77.7051,  8.7139],
  'vellore':           [79.1325, 12.9165],
  'kolkata':           [88.3639, 22.5726],
  'pune':              [73.8567, 18.5204],
  'ahmedabad':         [72.5714, 23.0225],
  'surat':             [72.8311, 21.1702],
  'jaipur':            [75.7873, 26.9124],
  'lucknow':           [80.9462, 26.8467],
  'kanpur':            [80.3319, 26.4499],
  'nagpur':            [79.0882, 21.1458],
  'indore':            [75.8577, 22.7196],
  'bhopal':            [77.4126, 23.2599],
  'visakhapatnam':     [83.2185, 17.6868],
  'vizag':             [83.2185, 17.6868],
  'patna':             [85.1376, 25.5941],
  'vadodara':          [73.1812, 22.3072],
  'baroda':            [73.1812, 22.3072],
  'ghaziabad':         [77.4538, 28.6692],
  'ludhiana':          [75.8573, 30.9010],
  'agra':              [78.0081, 27.1767],
  'nashik':            [73.7898, 19.9975],
  'faridabad':         [77.3178, 28.4089],
  'meerut':            [77.7060, 28.9845],
  'rajkot':            [70.8022, 22.3039],
  'varanasi':          [82.9739, 25.3176],
  'aurangabad':        [75.3433, 19.8762],
  'amritsar':          [74.8723, 31.6340],
  'allahabad':         [81.8463, 25.4358],
  'prayagraj':         [81.8463, 25.4358],
  'ranchi':            [85.3096, 23.3441],
  'jabalpur':          [79.9864, 23.1815],
  'gwalior':           [78.1828, 26.2183],
  'vijayawada':        [80.6480, 16.5062],
  'jodhpur':           [73.0243, 26.2389],
  'kochi':             [76.2673,  9.9312],
  'cochin':            [76.2673,  9.9312],
  'chandigarh':        [76.7794, 30.7333],
  'thiruvananthapuram':[76.9366,  8.5241],
  'trivandrum':        [76.9366,  8.5241],
  'mysuru':            [76.6394, 12.2958],
  'mysore':            [76.6394, 12.2958],
  'guwahati':          [91.7362, 26.1445],
  'hubballi':          [75.1239, 15.3647],
  'hubli':             [75.1239, 15.3647],
  'mangaluru':         [74.8558, 12.9141],
  'mangalore':         [74.8558, 12.9141],
  'tiruppur':          [77.3411, 11.1085],
  'tirupur':           [77.3411, 11.1085],
  'tirupati':          [79.4192, 13.6288],
  'guntur':            [80.4365, 16.2976],
  'nellore':           [79.9868, 14.4426],
  'kurnool':           [78.0373, 15.8281],
  'rajahmundry':       [81.7799, 17.0005],
  'kakinada':          [82.2475, 16.9891],
  'warangal':          [79.5941, 17.9784],
  'nizamabad':         [78.0941, 18.6725],
  'puducherry':        [79.8083, 11.9416],
  'pondicherry':       [79.8083, 11.9416],
  'thanjavur':         [79.1378, 10.7870],
  'tanjore':           [79.1378, 10.7870],
  'dindigul':          [77.9695, 10.3624],
  'thoothukudi':       [78.1348,  8.7642],
  'tuticorin':         [78.1348,  8.7642],
  'cuddalore':         [79.7648, 11.7447],
  'nagercoil':         [77.3169,  8.1833],
  'kancheepuram':      [79.7036, 12.8185],
  'kumbakonam':        [79.3886, 10.9617],
  'namakkal':          [78.1673, 11.2189],
  'karur':             [78.0800, 10.9601],
  'krishnagiri':       [78.2138, 12.5264],
  'dharmapuri':        [78.1580, 12.1285],
  'tiruvannamalai':    [79.0747, 12.2253],
  'tiruvallur':        [79.9099, 13.1436],
  'nagapattinam':      [79.8453, 10.7672],
  'villupuram':        [79.4933, 11.9389],
  'sivakasi':          [77.8050,  9.4528],
  'virudhunagar':      [77.9623,  9.5823],
  'ramanathapuram':    [78.8308,  9.3762],
  'perambalur':        [78.8808, 11.2337],
  'ariyalur':          [79.0761, 11.1400],
  'chengalpattu':      [79.9782, 12.6921],
  'mayiladuthurai':    [79.6491, 11.1027],
  'tiruvarur':         [79.6342, 10.7724],
  'bhubaneswar':       [85.8245, 20.2961],
  'cuttack':           [85.8782, 20.4625],
  'dehradun':          [78.0322, 30.3165],
  'raipur':            [81.6296, 21.2514],
  'gurgaon':           [77.0266, 28.4595],
  'gurugram':          [77.0266, 28.4595],
  'noida':             [77.3910, 28.5355],
  'gorakhpur':         [83.3732, 26.7606],
  'aligarh':           [78.0880, 27.8974],
  'bareilly':          [79.4304, 28.3670],
  'mathura':           [77.6737, 27.4924],
  'jhansi':            [78.5685, 25.4484],
  'rourkela':          [84.8536, 22.2604],
  'durgapur':          [87.3119, 23.5204],
  'kolhapur':          [74.2433, 16.7050],
  'solapur':           [75.9064, 17.6805],
  'amravati':          [77.7523, 20.9374],
  'nanded':            [77.2986, 19.1603],
  'latur':             [76.5604, 18.4088],
  'jalandhar':         [75.5762, 31.3260],
  'patiala':           [76.3869, 30.3398],
  'ajmer':             [74.6399, 26.4499],
  'kota':              [75.8648, 25.2138],
  'udaipur':           [73.6833, 24.5854],
  'shillong':          [91.8933, 25.5788],
  'agartala':          [91.2868, 23.8315],
}

const InstituteKPIGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 0 12px;
  margin-bottom: 8px;
`

const InstituteKPICard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s;
  cursor: pointer;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(50,73,215,0.10);
    border-color: ${p => p.$color || '#3249D7'};
  }
`

const IKPIIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${p => (p.$color || '#3249D7')}15;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: ${p => p.$color || '#3249D7'};
  flex-shrink: 0;
`

const IKPIValue = styled.div`
  font-size: 19px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.1;
  white-space: nowrap;
`

const IKPILabel = styled.div`
  font-size: 11px;
  color: #7b8499;
  margin-top: 2px;
  font-weight: 500;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 10px;
`

const TopStatesCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`

const StateBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`

const StateBarLabel = styled.div`
  font-size: 12px;
  color: #444;
  width: 140px;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StateBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: #f0f2f5;
  border-radius: 4px;
  overflow: hidden;
`

const StateBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3249D7, #6b7ff0);
  border-radius: 4px;
  width: ${p => p.$pct}%;
  transition: width 0.4s ease;
`

const StateBarValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
  width: 50px;
  text-align: right;
  flex-shrink: 0;
`

const StatesMapCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  align-items: stretch;
`
const MapPane = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
`
const MapPaneTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
`
const MapHint = styled.div`
  font-size: 11px;
  color: #7b8499;
  min-height: 18px;
  margin-top: 2px;
`

const FilterPopup = styled.div`
  display: flex;
  height: 360px;
  overflow: hidden;
`
const FilterSidebar = styled.div`
  width: 140px;
  border-right: 1px solid #f0f0f0;
  overflow-y: auto;
  flex-shrink: 0;
`
const FilterSidebarItem = styled.div`
  padding: 9px 14px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${p => p.$active ? '#f0f4ff' : 'transparent'};
  color: ${p => p.$active ? '#3249D7' : '#333'};
  font-weight: ${p => p.$active ? 600 : 400};
  border-right: ${p => p.$active ? '2px solid #3249D7' : '2px solid transparent'};
  &:hover { background: #f5f7ff; }
`
const FilterPanel = styled.div`
  flex: 1;
  padding: 12px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const FilterChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  min-height: 0;
`

const SummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 16px;
  padding: 14px 18px;
  background: #f8f9fc;
  border: 1px solid #e8ecf4;
  border-radius: 10px;
`

const PlacementMiniGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const PlacementMiniCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-left: 4px solid ${p => p.$accent || '#3249D7'};
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 130px;
  flex: 1;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`

const MiniCardValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: ${p => p.$color || '#1a1a2e'};
  line-height: 1.1;
`

const MiniCardLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #7b8499;
  margin-top: 2px;
`

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #444;
  strong { color: #1a1a2e; font-size: 16px; }
`

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`

const ChartCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`

const ChartTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 10px;
`

const GENDER_COLORS = ['#3249D7', '#f5222d', '#faad14']
const WLP_COLORS = ['#3249D7', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#fa8c16', '#1890ff']

const instituteColumns = (onViewSummary) => [
  {
    title: 'Institute Name',
    key: 'campus',
    render: (_, r) => (
      <div>
        <span
          style={{ color: '#3249D7', cursor: 'pointer', fontWeight: 500 }}
          onClick={() => onViewSummary(r)}
        >
          {r.campus_name}
        </span>
        {r.institute_name && r.institute_name !== r.campus_name && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{r.institute_name}</div>
        )}
      </div>
    ),
  },
  { title: 'State', dataIndex: 'state', key: 'state' },
  { title: 'City', dataIndex: 'city', key: 'city' },
  {
    title: 'Students',
    dataIndex: 'student_count',
    key: 'students',
    sorter: (a, b) => (a.student_count || 0) - (b.student_count || 0),
  },
  {
    title: 'Avg Marks',
    dataIndex: 'avg_marks',
    key: 'marks',
    render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—',
    sorter: (a, b) => (a.avg_marks || 0) - (b.avg_marks || 0),
  },
  {
    title: 'Departments',
    dataIndex: 'departments',
    key: 'depts',
  },
  {
    title: 'Action',
    key: 'action',
    render: (_, r) => (
      <Button size="small" type="link" onClick={() => onViewSummary(r)}>
        View Analytics
      </Button>
    ),
  },
]

const deptColumns = (onOpenProfileReady) => [
  { title: 'Department', dataIndex: 'department', key: 'dept', render: v => <strong>{v}</strong> },
  { title: 'Students', dataIndex: 'student_count', key: 'count', sorter: (a, b) => a.student_count - b.student_count },
  { title: 'Avg Marks', dataIndex: 'avg_marks', key: 'marks', render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
  { title: 'Highest', dataIndex: 'highest_marks', key: 'high', render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
  { title: 'Lowest', dataIndex: 'lowest_marks', key: 'low', render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
  { title: 'No Arrears', dataIndex: 'clear_arrears', key: 'ca' },
  { title: 'Female', dataIndex: 'female_count', key: 'female' },
  {
    title: 'Profile Ready', dataIndex: 'profile_ready_count', key: 'pr',
    render: (v, r) => {
      const count = v || 0
      const pct = r.student_count ? Math.round(count * 100 / r.student_count) : 0
      const label = r.student_count ? `${count} (${pct}%)` : `${count}`
      return count > 0
        ? <AntTooltip title="View profile-ready students"><Tag color="cyan" style={{ cursor: 'pointer' }} onClick={() => onOpenProfileReady(r)}>{label}</Tag></AntTooltip>
        : <span style={{ color: '#bbb' }}>{label}</span>
    },
  },
  { title: 'Assessed', dataIndex: 'assessed_count', key: 'asc', render: (v, r) => r.student_count ? `${v || 0} (${((v || 0) * 100 / r.student_count).toFixed(0)}%)` : v || 0 },
]

const genderColumns = [
  { title: 'Department', dataIndex: 'department', key: 'dept', render: v => <strong>{v}</strong> },
  { title: 'Male', dataIndex: 'male_count', key: 'male' },
  { title: 'Female', dataIndex: 'female_count', key: 'female' },
  { title: 'Other', dataIndex: 'other_count', key: 'other', render: v => v || 0 },
  { title: 'Total', dataIndex: 'total', key: 'total' },
  { title: 'Female %', dataIndex: 'female_pct', key: 'fpct', render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
]

const wlpColumns = [
  { title: 'Preferred State', dataIndex: 'preferred_state', key: 'state' },
  {
    title: 'Preference Count',
    dataIndex: 'preference_count',
    key: 'count',
    sorter: (a, b) => a.preference_count - b.preference_count,
    defaultSortOrder: 'descend',
    render: v => <Tag color="blue">{v}</Tag>,
  },
]

const InstituteTab = ({
  institutes,
  institutesLoading,
  instituteSummary,
  instituteSummaryLoading,
  institutePlacement,
  institutePlacementLoading,
  filterStates,
  filterDepartments,
  filterDegrees,
  fetchInstitutes,
  fetchInstituteSummary,
  fetchInstitutePlacement,
}) => {
  const [selectedInstitute, setSelectedInstitute] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_PENDING)
  const [pendingFilters, setPendingFilters] = useState(EMPTY_PENDING)
  const [filterOpen, setFilterOpen] = useState(false)
  const [activeCat, setActiveCat] = useState('state')
  const [catSearch, setCatSearch] = useState('')
  const [campusCities, setCampusCities] = useState([])
  const [instituteJobRoles, setInstituteJobRoles] = useState([])
  const [jobRolesLoading, setJobRolesLoading] = useState(false)
  const institutesTableRef = useRef(null)
  const [instituteCandidatesDrawer, setInstituteCandidatesDrawer] = useState({ open: false, title: '', data: [], loading: false })
  const [companyJobRolesDrawer, setCompanyJobRolesDrawer] = useState({ open: false, companyName: '', data: [], loading: false })
  const [roleCandidatesDrawer, setRoleCandidatesDrawer] = useState({ open: false, roleTitle: '', type: 'applied', data: [], loading: false })
  const [profileReadyDrawer, setProfileReadyDrawer] = useState({ open: false, dept: '', data: [], loading: false })
  const [hoveredState, setHoveredState] = useState(null)
  const [hoveredCity, setHoveredCity] = useState(null)
  const [stateMapMousePos, setStateMapMousePos] = useState(null)
  const [cityMapMousePos, setCityMapMousePos] = useState(null)
  const [deptBreakdown, setDeptBreakdown] = useState([])
  const [degreeBreakdown, setDegreeBreakdown] = useState([])
  const searchTimer = useRef(null)

  useEffect(() => {
    fetchInstitutes()
    adminRequest.get('/meta-dashboard/filters/campus-cities')
      .then(res => setCampusCities(res?.data?.data || []))
      .catch(() => {})
    adminRequest.get('/meta-dashboard/institute/dept-breakdown')
      .then(res => setDeptBreakdown(res?.data?.data || []))
      .catch(() => {})
    adminRequest.get('/meta-dashboard/institute/degree-breakdown')
      .then(res => setDegreeBreakdown(res?.data?.data || []))
      .catch(() => {})
  }, [])

  const buildPayload = (f, search) => {
    const p = {}
    if (f.state?.length)        p.state       = f.state
    if (f.city?.length)         p.city        = f.city
    if (f.dept?.length)         p.dept        = f.dept
    if (f.degree?.length)       p.degree      = f.degree
    if (f.gender?.length)       p.gender      = f.gender
    if (f.minMarks > 0)         p.minMarks    = f.minMarks
    if (f.maxMarks > 0)         p.maxMarks    = f.maxMarks
    if (f.maxArrears > 0)       p.maxArrears  = f.maxArrears
    if (f.minStudents > 0)      p.minStudents = f.minStudents
    if (f.assessed?.length)     p.assessed    = f.assessed
    if (f.profileReady?.length) p.profileReady = f.profileReady
    if (search)                 p.search      = search
    return p
  }

  const handleApplyFilter = () => {
    setAppliedFilters(pendingFilters)
    setFilterOpen(false)
    fetchInstitutes(buildPayload(pendingFilters, searchText))
  }

  const handleReset = () => {
    setAppliedFilters(EMPTY_PENDING)
    setPendingFilters(EMPTY_PENDING)
    setSearchText('')
    setFilterOpen(false)
    clearTimeout(searchTimer.current)
    fetchInstitutes({})
  }

  const handleRemoveChip = (key, val) => {
    const next = { ...appliedFilters }
    if (['minMarks', 'maxMarks', 'maxArrears', 'minStudents'].includes(key)) next[key] = undefined
    else next[key] = toArr(next[key]).filter(v => v !== val)
    setAppliedFilters(next)
    setPendingFilters(next)
    fetchInstitutes(buildPayload(next, searchText))
  }

  const toggleCheck = (key, val) => {
    setPendingFilters(prev => {
      const arr = toArr(prev[key])
      return { ...prev, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] }
    })
  }

  const catCount = key => {
    const v = pendingFilters[key]
    if (['minMarks', 'maxMarks', 'maxArrears', 'minStudents'].includes(key)) return v > 0 ? 1 : 0
    return toArr(v).length
  }

  const getCatOptions = key => {
    const opts = {
      state:        (filterStates     || []).map(v => ({ value: v, label: v })),
      city:         campusCities.map(v => ({ value: v, label: v })),
      dept:         (filterDepartments || []).map(v => ({ value: v, label: v })),
      degree:       (filterDegrees    || []).map(v => ({ value: v, label: v })),
      gender:       GENDER_OPTIONS,
      assessed:     ASSESSED_OPTIONS,
      profileReady: PROFILE_READY_OPTIONS,
      minMarks:     [],
      maxMarks:     [],
      maxArrears:   [],
      minStudents:  [],
    }[key] || []
    if (!catSearch.trim()) return opts
    const q = catSearch.toLowerCase()
    return opts.filter(o => o.label.toLowerCase().includes(q))
  }

  const activeChips = useMemo(() => {
    const chips = []
    const f = appliedFilters
    const optionMap = { gender: GENDER_OPTIONS, assessed: ASSESSED_OPTIONS, profileReady: PROFILE_READY_OPTIONS }
    ;['state', 'city', 'dept', 'degree', 'gender', 'assessed', 'profileReady'].forEach(key => {
      toArr(f[key]).forEach(val => {
        const label = optionMap[key]
          ? (optionMap[key].find(o => o.value === val)?.label || val)
          : val
        chips.push({ key, val, label: `${FILTER_CATS.find(c => c.key === key)?.label}: ${label}` })
      })
    })
    if (f.minMarks    > 0) chips.push({ key: 'minMarks',    val: f.minMarks,    label: `Min Marks: ${f.minMarks}%` })
    if (f.maxMarks    > 0) chips.push({ key: 'maxMarks',    val: f.maxMarks,    label: `Max Marks: ${f.maxMarks}%` })
    if (f.maxArrears  > 0) chips.push({ key: 'maxArrears',  val: f.maxArrears,  label: `Max Arrears: ${f.maxArrears}` })
    if (f.minStudents > 0) chips.push({ key: 'minStudents', val: f.minStudents, label: `Min Students: ${f.minStudents}` })
    return chips
  }, [appliedFilters])

  const totalActiveFilters = activeChips.length

  const handleSearchChange = (e) => {
    const val = e.target.value
    setSearchText(val)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      fetchInstitutes(buildPayload(appliedFilters, val || undefined))
    }, 350)
  }

  const openInstituteCandidatesDrawer = useCallback(async (type) => {
    if (!selectedInstitute) return
    const label = type === 'placed' ? 'Placed' : type === 'rejected' ? 'Not Selected' : 'Applied'
    setInstituteCandidatesDrawer({ open: true, title: `${label} Students — ${selectedInstitute.campus_name}`, data: [], loading: true })
    try {
      const res = await adminRequest.get(`/meta-dashboard/institutes/${selectedInstitute.institute_campus_id}/candidates?type=${type}`)
      setInstituteCandidatesDrawer(prev => ({ ...prev, data: res?.data?.data || [], loading: false }))
    } catch {
      setInstituteCandidatesDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [selectedInstitute])

  const openCompanyJobRolesDrawer = useCallback(async (company) => {
    setCompanyJobRolesDrawer({ open: true, companyName: company.company_name, data: [], loading: true })
    try {
      let endpoint
      if (company.company_type === 'Institute') {
        endpoint = `/meta-dashboard/institute-company/${company.id}/job-roles`
      } else {
        // Scope to this campus so the drawer count matches the table count
        const campusId = selectedInstitute?.institute_campus_id
        endpoint = campusId
          ? `/meta-dashboard/corporate/${company.id}/campus/${campusId}/job-roles`
          : `/meta-dashboard/corporate/${company.id}/job-roles`
      }
      const res = await adminRequest.get(endpoint)
      setCompanyJobRolesDrawer(prev => ({ ...prev, data: res?.data?.data || [], loading: false }))
    } catch (err) {
      console.error('[openCompanyJobRolesDrawer] error:', err)
      setCompanyJobRolesDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [selectedInstitute])

  const openProfileReadyDrawer = useCallback(async (deptRow) => {
    if (!selectedInstitute) return
    setProfileReadyDrawer({ open: true, dept: deptRow.department || '—', data: [], loading: true })
    try {
      const res = await adminRequest.post(
        `/meta-dashboard/institutes/${selectedInstitute.institute_campus_id}/students`,
        { dept: deptRow.department, profileReady: 'yes', limit: 500 },
      )
      const payload = res?.data?.data
      const rows = Array.isArray(payload) ? payload : (payload?.data || [])
      setProfileReadyDrawer(prev => ({ ...prev, data: rows, loading: false }))
    } catch {
      setProfileReadyDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [selectedInstitute])

  const openRoleCandidatesDrawer = useCallback(async (role, type) => {
    setRoleCandidatesDrawer({ open: true, roleTitle: role.title || '—', type, data: [], loading: true })
    try {
      // Job Roles Floated rows come from institute_campus_jobRole_map: `id` is the map-row id
      // while `job_id` is the actual corporate job-role id that student_role_mapping.role_id
      // references. The candidates endpoint filters on the job-role id, so pass job_id.
      const roleId = role.job_id || role.id
      const res = await adminRequest.get(`/meta-dashboard/job-roles/${roleId}/candidates?type=${type}`)
      setRoleCandidatesDrawer(prev => ({ ...prev, data: res?.data?.data || [], loading: false }))
    } catch {
      setRoleCandidatesDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [])

  const handleViewSummary = (institute) => {
    setSelectedInstitute(institute)
    fetchInstituteSummary(institute.institute_campus_id)
    fetchInstitutePlacement(institute.institute_campus_id)
    setInstituteJobRoles([])
    setJobRolesLoading(true)
    adminRequest.get(`/meta-dashboard/institutes/${institute.institute_campus_id}/job-roles`)
      .then(r => setInstituteJobRoles(r?.data?.data || []))
      .catch(() => {})
      .finally(() => setJobRolesLoading(false))
    setModalOpen(true)
  }

  const filteredInstitutes = institutes || []

  const instituteKPIs = useMemo(() => {
    const list = institutes || []
    const totalCampuses   = list.length
    // Prefer the backend's platform-wide count when present — it matches
    // the same number Candidate 360 / Corporate 360 show and includes
    // students who aren't linked to any campus. Fall back to summing
    // per-campus counts only when the field is missing (old backend).
    const totalStudents   = list[0]?.total_platform_students != null
      ? list[0].total_platform_students
      : list.reduce((s, i) => s + (i.student_count || 0), 0)
    const statesCovered   = new Set(list.map(i => i.state).filter(Boolean)).size
    const totalDepts      = list.reduce((s, i) => s + (i.departments || 0), 0)
    const platformAvg     = list.length ? parseFloat(list[0]?.platform_avg_marks) || 0 : 0
    const weightedMarks   = list.reduce((s, i) => s + (parseFloat(i.avg_marks) || 0) * (i.student_count || 0), 0)
    const avgMarks        = totalStudents > 0 ? (weightedMarks / totalStudents).toFixed(1) : null
    return { totalCampuses, totalStudents, statesCovered, totalDepts, platformAvg, avgMarks }
  }, [institutes])

  const summary = instituteSummary || {}

  const deptChartData = useMemo(() => {
    return (summary.department_stats || [])
      .map(d => ({
        dept: (d.department || '').length > 12 ? d.department.substring(0, 10) + '…' : (d.department || ''),
        Students: d.student_count || 0,
        'Avg Marks': parseFloat(d.avg_marks) || 0,
      }))
      .sort((a, b) => b.Students - a.Students)
      .slice(0, 10)
  }, [summary])

  const genderChartData = useMemo(() => {
    let male = 0, female = 0, other = 0, unknown = 0
    ;(summary.gender_by_dept || []).forEach(d => {
      male    += parseInt(d.male_count)    || 0
      female  += parseInt(d.female_count)  || 0
      other   += parseInt(d.other_count)   || 0
      // Students with no gender selected. Surfacing them as a slice keeps
      // the chart total aligned with the campus's student headcount —
      // previously the chart silently dropped 27 of 177 on Prabha College.
      unknown += parseInt(d.unknown_count) || 0
    })
    return [
      { name: 'Male', value: male },
      { name: 'Female', value: female },
      ...(other   > 0 ? [{ name: 'Other',   value: other   }] : []),
      ...(unknown > 0 ? [{ name: 'Unknown', value: unknown }] : []),
    ].filter(d => d.value > 0)
  }, [summary])

  const wlpChartData = useMemo(() => {
    return (summary.wlp_heatmap || [])
      .slice(0, 8)
      .map(w => ({
        state: (w.preferred_state || '').length > 12 ? w.preferred_state.substring(0, 10) + '…' : (w.preferred_state || ''),
        Count: w.preference_count || 0,
      }))
  }, [summary])

  const readinessChartData = useMemo(() => {
    return (summary.department_stats || [])
      .slice(0, 8)
      .map(d => ({
        dept: (d.department || '').length > 12 ? d.department.substring(0, 10) + '…' : (d.department || ''),
        Total: d.student_count || 0,
        'Profile Ready': d.profile_ready_count || 0,
        Assessed: d.assessed_count || 0,
      }))
  }, [summary])

  const kpiDefs = [
    { icon: <BankOutlined />,      label: 'Total Campuses',    value: instituteKPIs.totalCampuses,                                            color: '#3249D7' },
    { icon: <TeamOutlined />,      label: 'Total Students',    value: instituteKPIs.totalStudents.toLocaleString(),                            color: '#2e7d32' },
    { icon: <BarChartOutlined />,  label: 'Avg Marks',         value: instituteKPIs.avgMarks != null ? `${instituteKPIs.avgMarks}%` : '—',     color: '#0891b2' },
    { icon: <RiseOutlined />,      label: 'Platform Avg',      value: instituteKPIs.platformAvg ? `${parseFloat(instituteKPIs.platformAvg).toFixed(1)}%` : '—', color: '#3249D7' },
    { icon: <GlobalOutlined />,    label: 'States Covered',    value: instituteKPIs.statesCovered,                                            color: '#e65100' },
    { icon: <BookOutlined />,      label: 'Dept Enrollments',  value: instituteKPIs.totalDepts,                                               color: '#7c3aed' },
  ]

  const topStatesByStudents = useMemo(() => {
    const map = {}
    ;(institutes || []).forEach(inst => {
      const state = inst.state || 'Unknown'
      if (!map[state]) map[state] = { state, students: 0, campuses: 0, wMarks: 0, wCount: 0 }
      map[state].students += inst.student_count || 0
      map[state].campuses += 1
      if (inst.avg_marks != null) {
        map[state].wMarks += parseFloat(inst.avg_marks) * (inst.student_count || 0)
        map[state].wCount += inst.student_count || 0
      }
    })
    return Object.values(map)
      .map(s => ({ ...s, avg_marks: s.wCount > 0 ? parseFloat((s.wMarks / s.wCount).toFixed(1)) : null }))
      .sort((a, b) => b.students - a.students)
  }, [institutes])

  const normName = s => s.toLowerCase().replace(/[\s.&'()-]+/g, '')

  const topStatesColorMap = useMemo(() => {
    const m = {}
    topStatesByStudents.slice(0, 10).forEach((s, i) => {
      m[s.state.toLowerCase()] = { color: TOP10_COLORS[i], data: s }
      m[normName(s.state)]      = { color: TOP10_COLORS[i], data: s }
    })
    return m
  }, [topStatesByStudents])

  const resolveGeo = (geoName) => {
    const key    = geoName.toLowerCase()
    const mapped = STATE_ALIASES[key] || key
    return topStatesColorMap[mapped]
      || topStatesColorMap[normName(mapped)]
      || topStatesColorMap[key]
      || topStatesColorMap[normName(key)]
      || null
  }

  const getStateData  = (geoName) => resolveGeo(geoName)?.data  || null
  const getStateColor = (geoName) => resolveGeo(geoName)?.color || '#e8ecf4'

  const topCitiesByStudents = useMemo(() => {
    const map = {}
    ;(institutes || []).forEach(inst => {
      const city = inst.city || 'Unknown'
      if (!map[city]) map[city] = { city, students: 0, campuses: 0 }
      map[city].students += inst.student_count || 0
      map[city].campuses += 1
    })
    return Object.values(map).sort((a, b) => b.students - a.students).slice(0, 10)
  }, [institutes])


  return (
    <div>
      <InstituteKPIGrid>
        {kpiDefs.map((k, i) => (
          <AntTooltip key={i} title="Scroll to institutes list">
            <InstituteKPICard $color={k.color} onClick={() => institutesTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
              <IKPIIcon $color={k.color}>{k.icon}</IKPIIcon>
              <div>
                <IKPIValue>{institutesLoading ? '—' : k.value}</IKPIValue>
                <IKPILabel>{k.label}</IKPILabel>
              </div>
            </InstituteKPICard>
          </AntTooltip>
        ))}
      </InstituteKPIGrid>

      {/* Top States/Cities — India choropleth + city bubble map */}
      {topStatesByStudents.length > 0 && (
        <StatesMapCard>

          {/* Left: India choropleth map */}
          <MapPane
            onMouseMove={e => {
              const rect = e.currentTarget.getBoundingClientRect()
              setStateMapMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
            }}
            onMouseLeave={() => { setHoveredState(null); setStateMapMousePos(null) }}
          >
            <MapPaneTitle>Students Distribution by top 10 States</MapPaneTitle>
            <MapHint><span style={{ color: '#bbb' }}>Hover over a state</span></MapHint>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 800, center: [82, 22] }}
              width={800}
              height={420}
              style={{ width: '100%', height: 'auto' }}
            >
              <Geographies geography={indiaStatesRaw}>
                {({ geographies }) => geographies.map(geo => {
                  const data = getStateData(geo.properties.name || '')
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getStateColor(geo.properties.name || '')}
                      stroke="#9babc7"
                      strokeWidth={0.8}
                      onMouseEnter={() => setHoveredState(data ? { state: geo.properties.name, ...data } : { state: geo.properties.name, students: 0, campuses: 0 })}
                      onMouseLeave={() => setHoveredState(null)}
                      style={{
                        default: { outline: 'none' },
                        hover:   { filter: 'brightness(0.82)', outline: 'none', cursor: 'pointer' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  )
                })}
              </Geographies>
            </ComposableMap>
            {hoveredState && stateMapMousePos && (
              <div style={{
                position: 'absolute',
                left: stateMapMousePos.x + 14,
                top: stateMapMousePos.y - 14,
                background: 'rgba(26,26,46,0.9)',
                color: '#fff',
                padding: '5px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                pointerEvents: 'none',
                zIndex: 20,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
                {hoveredState.state}
                {hoveredState.students > 0
                  ? <>: <span style={{ color: '#93c5fd' }}>{hoveredState.students.toLocaleString()}</span> students · {hoveredState.campuses} campus{hoveredState.campuses !== 1 ? 'es' : ''}</>
                  : <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>— no data</span>}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 6 }}>
              {topStatesByStudents.slice(0, 10).map((s, i) => (
                <div key={s.state} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#444' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: TOP10_COLORS[i], flexShrink: 0 }} />
                  {s.state}
                </div>
              ))}
            </div>
          </MapPane>

          {/* Right: city bubble map */}
          <MapPane
            onMouseMove={e => {
              const rect = e.currentTarget.getBoundingClientRect()
              setCityMapMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
            }}
            onMouseLeave={() => { setHoveredCity(null); setCityMapMousePos(null) }}
          >
            <MapPaneTitle>Students Distribution by top 10 Cities</MapPaneTitle>
            <MapHint><span style={{ color: '#bbb' }}>Hover over a city bubble</span></MapHint>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 800, center: [82, 22] }}
              width={800}
              height={420}
              style={{ width: '100%', height: 'auto' }}
            >
              <Geographies geography={indiaStatesRaw}>
                {({ geographies }) => geographies.map(geo => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#eef2ff"
                    stroke="#9babc7"
                    strokeWidth={0.8}
                    style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                  />
                ))}
              </Geographies>
              {topCitiesByStudents.slice(0, 10).map((cityData, idx) => {
                const coords = CITY_COORDS[cityData.city.toLowerCase()]
                if (!coords) return null
                const maxCount = topCitiesByStudents[0]?.students || 1
                const r = 5 + Math.sqrt(cityData.students / maxCount) * 20
                const color = CITY_BUBBLE_COLORS[idx % CITY_BUBBLE_COLORS.length]
                return (
                  <Marker key={cityData.city} coordinates={coords}>
                    <circle
                      r={r}
                      fill={color}
                      fillOpacity={0.82}
                      stroke="#fff"
                      strokeWidth={1.5}
                      onMouseEnter={() => setHoveredCity(cityData)}
                      onMouseLeave={() => setHoveredCity(null)}
                      style={{ cursor: 'pointer' }}
                    />
                    <text
                      textAnchor="middle"
                      y={r + 9}
                      style={{ fontSize: 7, fill: '#1a1a2e', fontWeight: 700, pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {cityData.city}
                    </text>
                  </Marker>
                )
              })}
            </ComposableMap>
            {hoveredCity && cityMapMousePos && (
              <div style={{
                position: 'absolute',
                left: cityMapMousePos.x + 14,
                top: cityMapMousePos.y - 14,
                background: 'rgba(26,26,46,0.9)',
                color: '#fff',
                padding: '5px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                pointerEvents: 'none',
                zIndex: 20,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
                {hoveredCity.city}: <span style={{ color: '#93c5fd' }}>{hoveredCity.students.toLocaleString()}</span> students · {hoveredCity.campuses} campus{hoveredCity.campuses !== 1 ? 'es' : ''}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 6 }}>
              {topCitiesByStudents.slice(0, 10).map((c, i) => (
                CITY_COORDS[c.city.toLowerCase()] ? (
                  <div key={c.city} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#444' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: CITY_BUBBLE_COLORS[i % CITY_BUBBLE_COLORS.length], flexShrink: 0 }} />
                    {c.city}
                  </div>
                ) : null
              ))}
            </div>
          </MapPane>

          {/* ── Top Departments ─────────────────────────────── */}
          <MapPane>
            <MapPaneTitle>Top Departments by Students</MapPaneTitle>
            <MapHint><span style={{ color: '#bbb' }}>Institute-wide breakdown</span></MapHint>
            <div style={{ flex: 1, minHeight: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={deptBreakdown.map(d => ({
                  // `fullDept` keeps the original name for the tooltip; the
                  // displayed `dept` is truncated to fit the 130px Y-axis
                  // gutter without overflow.
                  fullDept: d.dept,
                  dept: d.dept.length > 18 ? d.dept.slice(0, 17) + '…' : d.dept,
                  Students: d.student_count,
                }))}
                margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                {/* interval={0} forces Recharts to render every category
                    label — by default it auto-skips ticks when row height
                    drops below its threshold, which silently hid 4-5 of the
                    10 department names on the Y-axis. */}
                <YAxis dataKey="dept" type="category" width={130} interval={0} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(v) => [v.toLocaleString(), 'Students']}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullDept || ''}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e8ecf4' }}
                />
                <Bar dataKey="Students" fill="#3249D7" radius={[0, 4, 4, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </MapPane>

          {/* ── Degree Distribution ─────────────────────────── */}
          <MapPane>
            <MapPaneTitle>Degree Distribution</MapPaneTitle>
            <MapHint><span style={{ color: '#bbb' }}>Student count by degree type</span></MapHint>
            <div style={{ flex: 1, minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={degreeBreakdown.map(d => ({ name: d.degree, value: d.student_count }))}
                  cx="50%" cy="45%"
                  innerRadius="38%" outerRadius="62%"
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => percent > 0.04 ? `${(percent * 100).toFixed(0)}%` : ''}
                  labelLine={false}
                >
                  {degreeBreakdown.map((_, i) => (
                    <Cell key={i} fill={TOP10_COLORS[i % TOP10_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, name) => [v.toLocaleString(), name]}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e8ecf4' }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
                  formatter={(name) => name.length > 16 ? name.slice(0, 15) + '…' : name}
                />
              </PieChart>
            </ResponsiveContainer>
            </div>
          </MapPane>

        </StatesMapCard>
      )}

      {/* Search + Filter bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: activeChips.length ? 8 : 14, alignItems: 'center' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder="Search by campus, institute, city…"
          value={searchText}
          onChange={handleSearchChange}
          allowClear
          onClear={() => {
            setSearchText('')
            clearTimeout(searchTimer.current)
            fetchInstitutes(buildPayload(appliedFilters, undefined))
          }}
          style={{ maxWidth: 380 }}
        />
        <Badge count={totalActiveFilters} size="small" offset={[-4, 4]}>
          <Button
            icon={<FilterOutlined />}
            onClick={() => { setPendingFilters(appliedFilters); setCatSearch(''); setActiveCat('state'); setFilterOpen(true) }}
            style={{
              borderRadius: 999,
              border: totalActiveFilters ? '2px solid #3249D7' : undefined,
              color: totalActiveFilters ? '#3249D7' : undefined,
              fontWeight: totalActiveFilters ? 600 : 400,
            }}
          >
            Filters
          </Button>
        </Badge>
        {totalActiveFilters > 0 && (
          <Button size="small" type="link" onClick={handleReset} style={{ color: '#888', fontSize: 12 }}>
            Clear all
          </Button>
        )}
        <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
          {(institutes || []).length} institute{(institutes || []).length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <FilterChipRow>
          {activeChips.map((chip, i) => (
            <Tag
              key={i}
              closable
              onClose={() => handleRemoveChip(chip.key, chip.val)}
              style={{ borderRadius: 999, fontSize: 12, padding: '2px 10px', background: '#eef0fb', border: '1px solid #c7d0f5', color: '#3249D7' }}
            >
              {chip.label}
            </Tag>
          ))}
        </FilterChipRow>
      )}

      {/* Filter popup */}
      <Modal
        open={filterOpen}
        onCancel={() => setFilterOpen(false)}
        title={<span style={{ fontSize: 14, fontWeight: 600 }}>Filter Institutes</span>}
        width={560}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => { setPendingFilters(EMPTY_PENDING); setCatSearch('') }}>Reset</Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => setFilterOpen(false)}>Cancel</Button>
              <Button type="primary" style={{ background: '#3249D7' }} onClick={handleApplyFilter}>
                Apply Filters
              </Button>
            </div>
          </div>
        }
        styles={{ body: { padding: 0 } }}
      >
        <FilterPopup>
          <FilterSidebar>
            {FILTER_CATS.map(cat => (
              <FilterSidebarItem
                key={cat.key}
                $active={activeCat === cat.key}
                onClick={() => { setActiveCat(cat.key); setCatSearch('') }}
              >
                {cat.label}
                {catCount(cat.key) > 0 && (
                  <span style={{ background: '#3249D7', color: '#fff', borderRadius: 999, fontSize: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>
                    {catCount(cat.key)}
                  </span>
                )}
              </FilterSidebarItem>
            ))}
          </FilterSidebar>
          <FilterPanel>
            {activeCat === 'minMarks' ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Show institutes where students average <strong>at least</strong>:</div>
                <InputNumber
                  min={0} max={100}
                  value={pendingFilters.minMarks}
                  onChange={v => setPendingFilters(prev => ({ ...prev, minMarks: v || undefined }))}
                  placeholder="e.g. 60"
                  addonAfter="%"
                  style={{ width: 160 }}
                />
              </div>
            ) : activeCat === 'maxMarks' ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Show institutes where students average <strong>at most</strong>:</div>
                <InputNumber
                  min={0} max={100}
                  value={pendingFilters.maxMarks}
                  onChange={v => setPendingFilters(prev => ({ ...prev, maxMarks: v || undefined }))}
                  placeholder="e.g. 80"
                  addonAfter="%"
                  style={{ width: 160 }}
                />
              </div>
            ) : activeCat === 'maxArrears' ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Show institutes where students have <strong>at most</strong> this many arrears:</div>
                <InputNumber
                  min={0} max={50}
                  value={pendingFilters.maxArrears}
                  onChange={v => setPendingFilters(prev => ({ ...prev, maxArrears: v || undefined }))}
                  placeholder="e.g. 2"
                  style={{ width: 160 }}
                />
              </div>
            ) : activeCat === 'minStudents' ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Show institutes with <strong>at least</strong> this many students:</div>
                <InputNumber
                  min={0} max={100000}
                  value={pendingFilters.minStudents}
                  onChange={v => setPendingFilters(prev => ({ ...prev, minStudents: v || undefined }))}
                  placeholder="e.g. 100"
                  style={{ width: 160 }}
                />
              </div>
            ) : (
              <>
                <Input
                  prefix={<SearchOutlined style={{ color: '#bbb', fontSize: 12 }} />}
                  placeholder={`Search ${FILTER_CATS.find(c => c.key === activeCat)?.label}…`}
                  value={catSearch}
                  onChange={e => setCatSearch(e.target.value)}
                  size="small"
                  allowClear
                  style={{ marginBottom: 6 }}
                />
                {/* Select-all / Clear toolbar — selects every option currently
                    visible after the search filter. */}
                {(() => {
                  const visible       = getCatOptions(activeCat)
                  const selectedSet   = new Set(toArr(pendingFilters[activeCat]))
                  const visibleValues = visible.map(o => o.value)
                  const visibleSelectedCount = visibleValues.filter(v => selectedSet.has(v)).length
                  const allVisibleSelected   = visible.length > 0 && visibleSelectedCount === visible.length
                  const someVisibleSelected  = visibleSelectedCount > 0 && !allVisibleSelected
                  const onToggleAll = () => {
                    setPendingFilters(prev => {
                      const current = new Set(toArr(prev[activeCat]))
                      if (allVisibleSelected) {
                        visibleValues.forEach(v => current.delete(v))
                      } else {
                        visibleValues.forEach(v => current.add(v))
                      }
                      return { ...prev, [activeCat]: Array.from(current) }
                    })
                  }
                  return visible.length === 0 ? null : (
                    <div style={{ padding: '4px 0 6px', borderBottom: '1px solid #f0f0f0', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Checkbox
                        indeterminate={someVisibleSelected}
                        checked={allVisibleSelected}
                        onChange={onToggleAll}
                      >
                        <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                          Select all ({visible.length})
                        </span>
                      </Checkbox>
                      {visibleSelectedCount > 0 && (
                        <Button
                          type="link"
                          size="small"
                          style={{ marginLeft: 'auto', padding: 0, fontSize: 12 }}
                          onClick={() => setPendingFilters(prev => ({ ...prev, [activeCat]: [] }))}
                        >
                          Clear ({visibleSelectedCount})
                        </Button>
                      )}
                    </div>
                  )
                })()}
                {getCatOptions(activeCat).length === 0 ? (
                  <div style={{ color: '#bbb', fontSize: 12, padding: '20px 0', textAlign: 'center' }}>No options</div>
                ) : (
                  getCatOptions(activeCat).map(opt => (
                    <div key={opt.value} style={{ padding: '4px 0' }}>
                      <Checkbox
                        checked={toArr(pendingFilters[activeCat]).includes(opt.value)}
                        onChange={() => toggleCheck(activeCat, opt.value)}
                      >
                        <span style={{ fontSize: 13 }}>{opt.label}</span>
                      </Checkbox>
                    </div>
                  ))
                )}
              </>
            )}
          </FilterPanel>
        </FilterPopup>
      </Modal>

      <div ref={institutesTableRef} />
      <Table
        rowKey="institute_campus_id"
        loading={institutesLoading}
        dataSource={filteredInstitutes}
        columns={instituteColumns(handleViewSummary)}
        size="small"
        scroll={{ x: 900 }}
        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['bottomRight'], showTotal: t => `${t} institutes` }}
      />

      <Modal
        title={selectedInstitute ? `${selectedInstitute.campus_name} — Analytics` : 'Institute Analytics'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={860}
      >
        {instituteSummaryLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <div>
            {selectedInstitute && (
              <SummaryBar>
                <SummaryItem>
                  <EnvironmentOutlined style={{ color: '#3249D7' }} />
                  {selectedInstitute.city}, {selectedInstitute.state}
                </SummaryItem>
                <SummaryItem>
                  <TeamOutlined style={{ color: '#2e7d32' }} />
                  <strong>{selectedInstitute.student_count}</strong> Students
                </SummaryItem>
                <SummaryItem>
                  <BookOutlined style={{ color: '#e65100' }} />
                  <strong>{selectedInstitute.departments}</strong> Departments
                </SummaryItem>
                <SummaryItem>
                  Avg Marks: <strong>{selectedInstitute.avg_marks != null ? `${parseFloat(selectedInstitute.avg_marks).toFixed(1)}%` : '—'}</strong>
                </SummaryItem>
                <SummaryItem>
                  <RiseOutlined style={{ color: selectedInstitute.avg_marks >= selectedInstitute.platform_avg_marks ? '#2e7d32' : '#c62828' }} />
                  vs Platform:{' '}
                  <strong style={{ color: selectedInstitute.avg_marks >= selectedInstitute.platform_avg_marks ? '#2e7d32' : '#c62828' }}>
                    {selectedInstitute.platform_avg_marks != null ? `${parseFloat(selectedInstitute.platform_avg_marks).toFixed(1)}%` : '—'}
                  </strong>
                </SummaryItem>
              </SummaryBar>
            )}

            <SectionHeader><SectionTitle>Placement Metrics</SectionTitle></SectionHeader>
            {institutePlacementLoading ? (
              <div style={{ textAlign: 'center', padding: '12px 0' }}><Spin size="small" /></div>
            ) : (
              <PlacementMiniGrid>
                <AntTooltip title={(institutePlacement?.stats?.applied_count ?? 0) > 0 ? 'View applied students' : null}>
                  <PlacementMiniCard $accent="#0891b2" style={{ cursor: (institutePlacement?.stats?.applied_count ?? 0) > 0 ? 'pointer' : 'default' }} onClick={() => (institutePlacement?.stats?.applied_count ?? 0) > 0 && openInstituteCandidatesDrawer('applied')}>
                    <FileSearchOutlined style={{ fontSize: 20, color: '#0891b2' }} />
                    <div>
                      <MiniCardValue $color="#0891b2">{institutePlacement?.stats?.applied_count ?? '—'}</MiniCardValue>
                      <MiniCardLabel>Applied</MiniCardLabel>
                    </div>
                  </PlacementMiniCard>
                </AntTooltip>
                <AntTooltip title={(institutePlacement?.stats?.placed_count ?? 0) > 0 ? 'View placed students' : null}>
                  <PlacementMiniCard $accent="#059669" style={{ cursor: (institutePlacement?.stats?.placed_count ?? 0) > 0 ? 'pointer' : 'default' }} onClick={() => (institutePlacement?.stats?.placed_count ?? 0) > 0 && openInstituteCandidatesDrawer('placed')}>
                    <CheckCircleOutlined style={{ fontSize: 20, color: '#059669' }} />
                    <div>
                      <MiniCardValue $color="#059669">{institutePlacement?.stats?.placed_count ?? '—'}</MiniCardValue>
                      <MiniCardLabel>Placed</MiniCardLabel>
                    </div>
                  </PlacementMiniCard>
                </AntTooltip>
                <AntTooltip title={(institutePlacement?.stats?.rejected_count ?? 0) > 0 ? 'View not-selected students' : null}>
                  <PlacementMiniCard $accent="#e65100" style={{ cursor: (institutePlacement?.stats?.rejected_count ?? 0) > 0 ? 'pointer' : 'default' }} onClick={() => (institutePlacement?.stats?.rejected_count ?? 0) > 0 && openInstituteCandidatesDrawer('rejected')}>
                    <CloseCircleOutlined style={{ fontSize: 20, color: '#e65100' }} />
                    <div>
                      <MiniCardValue $color="#e65100">{institutePlacement?.stats?.rejected_count ?? '—'}</MiniCardValue>
                      <MiniCardLabel>Not Selected</MiniCardLabel>
                    </div>
                  </PlacementMiniCard>
                </AntTooltip>
              </PlacementMiniGrid>
            )}

            {(institutePlacement?.companies || []).length > 0 && (
              <>
                <SectionHeader><SectionTitle>Companies Recruiting</SectionTitle></SectionHeader>
                <Table
                  rowKey="id"
                  dataSource={institutePlacement.companies}
                  size="small"
                  pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'] }}
                  columns={[
                    { title: 'Company', dataIndex: 'company_name', key: 'co', render: v => <strong>{v}</strong>, ellipsis: true },
                    {
                      title: 'Type', dataIndex: 'company_type', key: 'type', width: 100,
                      render: v => <Tag color={v === 'Institute' ? 'orange' : 'blue'} style={{ fontSize: 11 }}>{v || 'Corporate'}</Tag>,
                      filters: [{ text: 'Corporate', value: 'Corporate' }, { text: 'Institute', value: 'Institute' }],
                      onFilter: (val, record) => (record.company_type || 'Corporate') === val,
                    },
                    {
                      title: 'Job Roles', dataIndex: 'job_roles_count', key: 'roles', width: 90,
                      render: (v, record) => v > 0
                        ? <AntTooltip title="View job roles"><Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => openCompanyJobRolesDrawer(record)}>{v}</Tag></AntTooltip>
                        : <Tag>{v}</Tag>,
                    },
                    { title: 'Placed', dataIndex: 'placed_count', key: 'placed', width: 80, render: v => <Tag color="green">{v}</Tag>, sorter: (a, b) => (a.placed_count || 0) - (b.placed_count || 0), defaultSortOrder: 'descend' },
                  ]}
                />
              </>
            )}

            <SectionHeader>
              <SectionTitle><SolutionOutlined style={{ marginRight: 6 }} />Job Roles Floated</SectionTitle>
            </SectionHeader>
            {jobRolesLoading ? (
              <div style={{ textAlign: 'center', padding: '12px 0' }}><Spin size="small" /></div>
            ) : instituteJobRoles.length === 0 ? (
              <div style={{ color: '#bbb', fontSize: 12, marginBottom: 12 }}>No job roles mapped to this campus.</div>
            ) : (
              <Table
                rowKey="id"
                dataSource={instituteJobRoles}
                size="small"
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'] }}
                columns={[
                  {
                    title: 'Job Title',
                    dataIndex: 'title',
                    key: 'title',
                    ellipsis: true,
                    render: v => <strong>{v || '—'}</strong>,
                  },
                  { title: 'Company', dataIndex: 'company_name', key: 'co', ellipsis: true, width: 140, render: v => v || '—' },
                  {
                    title: 'Type',
                    key: 'type',
                    width: 100,
                    render: (_, r) => r.employment_type
                      ? <Tag>{r.employment_type.replace(/_/g, ' ')}</Tag>
                      : '—',
                  },
                  {
                    // Same rule as corporate-react Preview/CompensationSection:
                    // single value when min == max, range only when they
                    // actually differ. Falls back to whichever is set when
                    // only one side has a number.
                    title: 'CTC',
                    key: 'ctc',
                    width: 110,
                    render: (_, r) => {
                      const fmt = v => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                      const min = Number(r.min_compensation)
                      const max = Number(r.max_compensation)
                      const hasMin = Number.isFinite(min) && min > 0
                      const hasMax = Number.isFinite(max) && max > 0
                      if (!hasMin && !hasMax) return '—'
                      if (hasMin && hasMax && min === max) return fmt(min)
                      if (hasMin && hasMax) return `${fmt(min)} – ${fmt(max)}`
                      return fmt(hasMax ? max : min)
                    },
                  },
                  {
                    // Backend now emits role_status_label as one of
                    // DRAFT / ACTIVE / CLOSED / UNKNOWN, derived from
                    // jr.status (numeric: 0/1/-1) plus end_date check.
                    // Falls back to the old role_status enum if a row
                    // somehow predates the new column.
                    title: 'Role Status',
                    key: 'rstatus',
                    width: 130,
                    render: (_, r) => {
                      const v = r.role_status_label || r.role_status
                      if (!v) return '—'
                      const COLOR = {
                        ACTIVE: 'green', DRAFT: 'default', CLOSED: 'red', UNKNOWN: 'default',
                        OPEN_FOR_APPLICATION: 'green', UPCOMING: 'blue', EVALUATION_IN_PROGRESS: 'orange',
                        APPLICATION_CLOSED: 'default', COMPLETED: 'cyan', ROLE_CLOSED: 'red',
                      }
                      return <Tag color={COLOR[v] || 'default'}>{String(v).replace(/_/g, ' ')}</Tag>
                    },
                  },
                  {
                    // Backend renamed the field from `status` to `map_status`
                    // to avoid colliding with the role's own numeric status.
                    // Values (numeric): 1 = TPO accepted, 0 = pending review,
                    // -1 = rejected.
                    title: 'Map Status',
                    dataIndex: 'map_status',
                    key: 'mstatus',
                    width: 90,
                    render: v => {
                      const n = v == null ? null : Number(v)
                      const MAP = {
                        1:  ['Active',   'green'],
                        0:  ['Pending',  'orange'],
                        '-1': ['Rejected', 'red'],
                      }
                      const [label, color] = MAP[n] || ['Unknown', 'default']
                      return <Tag color={color}>{label}</Tag>
                    },
                  },
                  {
                    title: 'Candidates',
                    dataIndex: 'candidates',
                    key: 'cands',
                    width: 90,
                    render: (v, record) => v > 0
                      ? <AntTooltip title="View applied students"><Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => openRoleCandidatesDrawer(record, 'applied')}>{v}</Tag></AntTooltip>
                      : (v ?? '—'),
                  },
                ]}
              />
            )}

            {deptChartData.length > 0 && (
              <ChartsGrid>
                <ChartCard style={{ gridColumn: '1 / -1' }}>
                  <ChartTitle>Department Enrollment & Avg Marks</ChartTitle>
                  <ResponsiveContainer width="100%" height={230}>
                    <ComposedChart data={deptChartData} margin={{ top: 4, right: 30, left: -10, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="dept" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                      <Tooltip />
                      <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="left" dataKey="Students" fill="#3249D7" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="Avg Marks" stroke="#52c41a" strokeWidth={2} dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>

                {genderChartData.length > 0 && (
                  <ChartCard>
                    <ChartTitle>Gender Distribution</ChartTitle>
                    <ResponsiveContainer width="100%" height={210}>
                      <PieChart>
                        <Pie
                          data={genderChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={85}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {genderChartData.map((_, i) => (
                            <Cell key={i} fill={GENDER_COLORS[i % GENDER_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {wlpChartData.length > 0 && (
                  <ChartCard>
                    <ChartTitle>Top Work Location Preferences</ChartTitle>
                    <ResponsiveContainer width="100%" height={210}>
                      <BarChart data={wlpChartData} layout="vertical" margin={{ top: 4, right: 20, left: 10, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="state" tick={{ fontSize: 11 }} width={80} />
                        <Tooltip />
                        <Bar dataKey="Count" fill="#3249D7" radius={[0, 4, 4, 0]}>
                          {wlpChartData.map((_, i) => (
                            <Cell key={i} fill={WLP_COLORS[i % WLP_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {readinessChartData.length > 0 && (
                  <ChartCard style={{ gridColumn: '1 / -1' }}>
                    <ChartTitle>Placement Readiness Funnel by Department</ChartTitle>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={readinessChartData} margin={{ top: 4, right: 20, left: -10, bottom: 48 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="dept" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Total" fill="#3249D7" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Profile Ready" fill="#0891b2" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Assessed" fill="#059669" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </ChartsGrid>
            )}

            <Tabs defaultActiveKey="dept">
              <TabPane tab="Department Breakdown" key="dept">
                <Table
                  rowKey="department"
                  dataSource={summary.department_stats || []}
                  columns={deptColumns(openProfileReadyDrawer)}
                  size="small"
                  pagination={false}
                />
              </TabPane>

              <TabPane tab="Gender by Department" key="gender">
                <Table
                  rowKey="department"
                  dataSource={summary.gender_by_dept || []}
                  columns={genderColumns}
                  size="small"
                  pagination={false}
                />
              </TabPane>

              <TabPane tab="Work Location Preference" key="wlp">
                <Table
                  rowKey="preferred_state"
                  dataSource={summary.wlp_heatmap || []}
                  columns={wlpColumns}
                  size="small"
                  pagination={false}
                />
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* Institute candidates (Applied / Placed / Not Selected) */}
      <Drawer
        title={instituteCandidatesDrawer.title}
        placement="right"
        width={780}
        open={instituteCandidatesDrawer.open}
        onClose={() => setInstituteCandidatesDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {instituteCandidatesDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>
        ) : instituteCandidatesDrawer.data.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', paddingTop: 60 }}>No students found</div>
        ) : (
          <>
            <div style={{ marginBottom: 10, fontSize: 12, color: '#888' }}>{instituteCandidatesDrawer.data.length} student{instituteCandidatesDrawer.data.length !== 1 ? 's' : ''}</div>
            <Table
              rowKey={(r, i) => r.id || i}
              dataSource={instituteCandidatesDrawer.data}
              size="small"
              scroll={{ x: 750 }}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'], showTotal: t => `${t} total` }}
              columns={[
                { title: 'Name', dataIndex: 'full_name', key: 'name', ellipsis: true, width: 150, render: v => <strong style={{ fontSize: 13 }}>{v || '—'}</strong> },
                { title: 'Dept', dataIndex: 'department', key: 'dept', width: 100, ellipsis: true },
                { title: 'Marks', dataIndex: 'diploma_marks', key: 'marks', width: 70, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
                { title: 'Arrears', dataIndex: 'no_of_arrears', key: 'arr', width: 65, render: v => <Tag color={v === 0 ? 'green' : v <= 2 ? 'orange' : 'red'} style={{ fontSize: 11 }}>{v ?? '—'}</Tag> },
                { title: 'Role', dataIndex: 'role_title', key: 'role', ellipsis: true, width: 150 },
                {
                  title: 'Status', dataIndex: 'offer_status', key: 'status', width: 100,
                  render: v => {
                    const color = v === 'JOINED' ? 'green' : v === 'ACCEPTED' ? 'cyan' : v === 'RELEASED' ? 'blue' : v === 'REJECTED' ? 'red' : 'default'
                    return <Tag color={color} style={{ fontSize: 10 }}>{(v || '—').replace(/_/g, ' ')}</Tag>
                  },
                },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* Profile-ready students for a department */}
      <Drawer
        title={`Profile-Ready Students — ${profileReadyDrawer.dept}`}
        placement="right"
        width={780}
        open={profileReadyDrawer.open}
        onClose={() => setProfileReadyDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {profileReadyDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>
        ) : profileReadyDrawer.data.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', paddingTop: 60 }}>No profile-ready students in this department</div>
        ) : (
          <>
            <div style={{ marginBottom: 10, fontSize: 12, color: '#888' }}>{profileReadyDrawer.data.length} student{profileReadyDrawer.data.length !== 1 ? 's' : ''}</div>
            <Table
              rowKey={(r, i) => r.id || i}
              dataSource={profileReadyDrawer.data}
              size="small"
              scroll={{ x: 720 }}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'], showTotal: t => `${t} total` }}
              columns={[
                { title: 'Name', dataIndex: 'full_name', key: 'name', ellipsis: true, width: 160, render: v => <strong style={{ fontSize: 13 }}>{v || '—'}</strong> },
                { title: 'Dept', dataIndex: 'department', key: 'dept', width: 110, ellipsis: true },
                { title: 'Degree', dataIndex: 'degree', key: 'degree', width: 100, ellipsis: true },
                { title: 'Marks', dataIndex: 'diploma_marks', key: 'marks', width: 70, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
                { title: 'Arrears', dataIndex: 'no_of_arrears', key: 'arr', width: 65, render: v => <Tag color={v === 0 ? 'green' : v <= 2 ? 'orange' : 'red'} style={{ fontSize: 11 }}>{v ?? '—'}</Tag> },
                { title: 'Email', dataIndex: 'primary_email', key: 'email', ellipsis: true, width: 180 },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* Company job roles */}
      <Drawer
        title={`Job Roles — ${companyJobRolesDrawer.companyName}`}
        placement="right"
        width={640}
        open={companyJobRolesDrawer.open}
        onClose={() => setCompanyJobRolesDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {companyJobRolesDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>
        ) : companyJobRolesDrawer.data.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', paddingTop: 60 }}>No job roles found</div>
        ) : (
          <Table
            rowKey="id"
            dataSource={companyJobRolesDrawer.data}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'] }}
            columns={[
              { title: 'Role', dataIndex: 'title', key: 'title', ellipsis: true, render: v => <strong>{v || '—'}</strong> },
              { title: 'Type', dataIndex: 'employment_type', key: 'type', width: 100, render: v => v ? <Tag style={{ fontSize: 11 }}>{v.replace(/_/g, ' ')}</Tag> : '—' },
              { title: 'Applied', dataIndex: 'applied_count', key: 'applied', width: 75, render: v => <Tag color="blue">{v}</Tag> },
              { title: 'Placed', dataIndex: 'placed_count', key: 'placed', width: 70, render: v => <Tag color="green">{v}</Tag> },
              { title: 'Status', dataIndex: 'status', key: 'status', width: 75, render: v => <Tag color={v === 1 ? 'green' : v === -1 ? 'red' : 'default'}>{v === 1 ? 'Active' : v === -1 ? 'Closed' : 'Draft'}</Tag> },
            ]}
          />
        )}
      </Drawer>

      {/* Role candidates */}
      <Drawer
        title={`${roleCandidatesDrawer.type === 'placed' ? 'Placed' : 'Applied'} Students — ${roleCandidatesDrawer.roleTitle}`}
        placement="right"
        width={760}
        open={roleCandidatesDrawer.open}
        onClose={() => setRoleCandidatesDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {roleCandidatesDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>
        ) : roleCandidatesDrawer.data.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', paddingTop: 60 }}>No students found</div>
        ) : (
          <>
            <div style={{ marginBottom: 10, fontSize: 12, color: '#888' }}>{roleCandidatesDrawer.data.length} student{roleCandidatesDrawer.data.length !== 1 ? 's' : ''}</div>
            <Table
              rowKey={(r, i) => r.id || i}
              dataSource={roleCandidatesDrawer.data}
              size="small"
              scroll={{ x: 700 }}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'], showTotal: t => `${t} total` }}
              columns={[
                { title: 'Name', dataIndex: 'full_name', key: 'name', ellipsis: true, width: 150, render: v => <strong style={{ fontSize: 13 }}>{v || '—'}</strong> },
                { title: 'College', dataIndex: 'institute_campus_name', key: 'college', ellipsis: true, width: 150 },
                { title: 'Dept', dataIndex: 'department', key: 'dept', width: 90, ellipsis: true },
                { title: 'Marks', dataIndex: 'diploma_marks', key: 'marks', width: 70, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
                { title: 'Arrears', dataIndex: 'no_of_arrears', key: 'arr', width: 65, render: v => <Tag color={v === 0 ? 'green' : v <= 2 ? 'orange' : 'red'} style={{ fontSize: 11 }}>{v ?? '—'}</Tag> },
                {
                  title: 'Status', dataIndex: 'offer_status', key: 'status', width: 100,
                  render: v => {
                    const color = v === 'JOINED' ? 'green' : v === 'ACCEPTED' ? 'cyan' : v === 'RELEASED' ? 'blue' : v === 'REJECTED' ? 'red' : 'default'
                    return <Tag color={color} style={{ fontSize: 10 }}>{(v || '—').replace(/_/g, ' ')}</Tag>
                  },
                },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  )
}

export default InstituteTab
